import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { RedOpportunita } from '../../types/red'
import { STATO_OPPORTUNITA_CONFIG, FONTI_OPPORTUNITA_LABEL, EUR } from '../../constants/red'
import { redApi } from '../../services/api'

const RedOpportunitaPage: React.FC = () => {
  const [opportunita, setOpportunita] = useState<RedOpportunita[]>([])
  const [loading, setLoading] = useState(true)
  const [filtroStato, setFiltroStato] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ nome_riferimento: '', indirizzo: '', comune: '', provincia: '', fonte: '', prezzo_richiesto: '', prezzo_offerto: '', data_offerta: '', probabilita_chiusura: '0', note_due_diligence: '' })
  const navigate = useNavigate()

  const load = () => {
    redApi.opportunita.list(filtroStato || undefined).then(res => {
      setOpportunita(res.data)
      setLoading(false)
    })
  }

  useEffect(load, [filtroStato])

  const handleCreate = async () => {
    if (!form.nome_riferimento.trim()) { toast.error('Nome obbligatorio'); return }
    try {
      await redApi.opportunita.create({
        nome_riferimento: form.nome_riferimento.trim(),
        indirizzo: form.indirizzo || null,
        comune: form.comune || null,
        provincia: form.provincia || null,
        fonte: form.fonte || null,
        prezzo_richiesto: form.prezzo_richiesto ? parseFloat(form.prezzo_richiesto) : null,
        prezzo_offerto: form.prezzo_offerto ? parseFloat(form.prezzo_offerto) : null,
        data_offerta: form.data_offerta || null,
        probabilita_chiusura: parseInt(form.probabilita_chiusura, 10),
        note_due_diligence: form.note_due_diligence || null,
      })
      toast.success('Opportunita creata')
      setShowForm(false)
      setForm({ nome_riferimento: '', indirizzo: '', comune: '', provincia: '', fonte: '', prezzo_richiesto: '', prezzo_offerto: '', data_offerta: '', probabilita_chiusura: '0', note_due_diligence: '' })
      load()
    } catch { toast.error('Errore creazione') }
  }

  if (loading) return <div className="p-8 text-gray-500">Caricamento...</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pipeline Opportunità</h1>
          <p className="text-sm text-gray-500 mt-1">{opportunita.length} opportunità nel funnel</p>
        </div>
        <div className="flex items-center gap-3">
          <select className="select text-sm" value={filtroStato} onChange={e => setFiltroStato(e.target.value)}>
            <option value="">Tutti gli stati</option>
            {Object.keys(STATO_OPPORTUNITA_CONFIG).map(k => <option key={k} value={k}>{STATO_OPPORTUNITA_CONFIG[k].label}</option>)}
          </select>
          <button onClick={() => setShowForm(true)} className="btn-primary">+ Nuova</button>
        </div>
      </div>

      {showForm && (
        <div className="card p-5 mb-6 max-w-2xl">
          <h2 className="text-lg font-semibold mb-4">Nuova Opportunità</h2>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div><label className="label">Nome *</label><input className="input" value={form.nome_riferimento} onChange={e => setForm({...form, nome_riferimento: e.target.value})} /></div>
            <div><label className="label">Indirizzo</label><input className="input" value={form.indirizzo} onChange={e => setForm({...form, indirizzo: e.target.value})} /></div>
            <div><label className="label">Comune</label><input className="input" value={form.comune} onChange={e => setForm({...form, comune: e.target.value})} /></div>
            <div><label className="label">Provincia</label><input className="input" value={form.provincia} onChange={e => setForm({...form, provincia: e.target.value})} /></div>
            <div><label className="label">Fonte</label>
              <select className="select" value={form.fonte} onChange={e => setForm({...form, fonte: e.target.value})}>
                <option value="">—</option>
                {Object.entries(FONTI_OPPORTUNITA_LABEL).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label className="label">Prezzo richiesto</label><input type="number" className="input" value={form.prezzo_richiesto} onChange={e => setForm({...form, prezzo_richiesto: e.target.value})} /></div>
            <div><label className="label">Prezzo offerto</label><input type="number" className="input" value={form.prezzo_offerto} onChange={e => setForm({...form, prezzo_offerto: e.target.value})} /></div>
            <div><label className="label">Data offerta</label><input type="date" className="input" value={form.data_offerta} onChange={e => setForm({...form, data_offerta: e.target.value})} /></div>
            <div><label className="label">Probabilità %</label><input type="number" min="0" max="100" className="input" value={form.probabilita_chiusura} onChange={e => setForm({...form, probabilita_chiusura: e.target.value})} /></div>
          </div>
          <div className="mb-3"><label className="label">Note due diligence</label><textarea className="input" rows={2} value={form.note_due_diligence} onChange={e => setForm({...form, note_due_diligence: e.target.value})} /></div>
          <div className="flex gap-3">
            <button onClick={() => setShowForm(false)} className="btn-secondary">Annulla</button>
            <button onClick={handleCreate} className="btn-primary">Crea</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {opportunita.map(opp => {
          const cfg = STATO_OPPORTUNITA_CONFIG[opp.stato]
          return (
            <div key={opp.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900">{opp.nome_riferimento}</h3>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ color: cfg.color, backgroundColor: '#f3f4f6' }}>{cfg.label}</span>
                  {opp.immobile_id && <span className="text-xs text-green-600">✓ Acquisita</span>}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {opp.indirizzo && `${opp.indirizzo}, `}{opp.comune} {opp.provincia && `(${opp.provincia})`}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  {opp.fonte && `Fonte: ${FONTI_OPPORTUNITA_LABEL[opp.fonte] || opp.fonte}`}
                  {opp.prezzo_richiesto != null && ` · Richiesto: ${EUR.format(opp.prezzo_richiesto)}`}
                  {opp.prezzo_offerto != null && ` · Offerto: ${EUR.format(opp.prezzo_offerto)}`}
                  {opp.probabilita_chiusura != null && ` · Probabilità: ${opp.probabilita_chiusura}%`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {opp.immobile_id ? (
                  <button onClick={() => navigate(`/red/${opp.immobile_id}`)} className="text-xs text-blue-600 hover:underline">Vedi immobile</button>
                ) : (
                  <button
                    onClick={async () => {
                      if (!confirm('Convertire questa opportunità in un immobile del portfolio?')) return
                      try {
                        const res = await redApi.opportunita.converti(opp.id)
                        toast.success('Immobile creato')
                        navigate(`/red/${res.data.id}`)
                      } catch {
                        toast.error('Errore conversione')
                      }
                    }}
                    className="text-xs text-emerald-600 hover:underline font-medium"
                  >
                    Converti in Immobile
                  </button>
                )}
                <button onClick={async () => { if (!confirm('Eliminare?')) return; await redApi.opportunita.delete(opp.id); toast.success('Eliminata'); load() }} className="text-xs text-red-500 hover:text-red-700">🗑</button>
              </div>
            </div>
          )
        })}
        {opportunita.length === 0 && <div className="text-center py-20 text-gray-400">Nessuna opportunità nel pipeline.</div>}
      </div>
    </div>
  )
}

export default RedOpportunitaPage
